/* eslint max-lines-per-function: ["error", {max: 180}]  -- Long function due to complex logic */
import { useCallback, useMemo } from "react";

import {
  AnalyticsFieldName,
  GroupClassSeriesAnalyticsEvent,
  GroupClassSeriesAnalyticsSources,
} from "@/constants/analytics/Analytics";
import { ModalPath } from "@/constants/Navigation";
import { GroupClassCarouselVariantValues } from "@/constants/Schedule";
import { ExploreTestID } from "@/constants/TestID";
import useFeatureFlags from "@/hooks/useFeatureFlags";
import { useAnalytics } from "@/services/Analytics";
import { useAppUserSelector } from "@/store/reducers/appUser";
import useGroupClassCarouselContent from "@/ui/components/explore/GroupClassSeriesCarousel/useGroupClassCarouselContent";
import useGroupClassCarouselHeader from "@/ui/components/explore/GroupClassSeriesCarousel/useGroupClassCarouselHeader";
import YourGroupsEmptyListCard from "@/ui/components/explore/GroupClassSeriesCarousel/YourGroupsEmptyListCard";
import { ActionBarMessages } from "@/ui/components/groupClasses/series/details/ActionBars";
import { PreviewCardSize } from "@/ui/components/groupClasses/series/preview/GroupClassSeriesPreviewCard";
import { GroupClassSeriesPreviewList } from "@/ui/components/groupClasses/series/preview/GroupClassSeriesPreviewList";
import { ActionBar, FAIconName, SectionHeader } from "@/ui/core";
import Spinner from "@/ui/core/Spinner";
import { Box, CarouselWrapper, VStack } from "@/ui/layout";
import { navigateWithParentSelectedUserId } from "@/utils/navigation.util";
import { joinTestIDs } from "@/utils/string.util";

interface GroupClassSeriesCarouselProps {
  analyticsSource: GroupClassSeriesAnalyticsSources;
  hasPaddingTop?: boolean;
  tabSource: "explore" | "home";
  variant: GroupClassCarouselVariant;
}

const buildCarouselTestID = (variant: GroupClassCarouselVariant) =>
  joinTestIDs(ExploreTestID.GroupClassSeriesCarousel, variant);

const GroupClassSeriesCarousel = ({
  analyticsSource,
  hasPaddingTop = true,
  tabSource,
  variant,
}: GroupClassSeriesCarouselProps) => {
  const { track } = useAnalytics();
  const { isGroupClassRedesignEnabled } = useFeatureFlags();

  const {
    onUserChange,
    selectedCarouselUser: {
      isInTreatment: isSelectedUserInTreatment,
      userId: selectedUserId,
      userName: forWhom,
    },
    title,
    userTabs,
  } = useGroupClassCarouselHeader({ analyticsSource, variant });

  const handleSearchGroups = useCallback(() => {
    track(
      GroupClassSeriesAnalyticsEvent.YourGroupsEmptyCardSearchGroupsPressed,
      {
        [AnalyticsFieldName.Component]:
          GroupClassSeriesAnalyticsSources.GroupClassSeriesCarousel,
        [AnalyticsFieldName.Source]: analyticsSource,
      },
    );
    navigateWithParentSelectedUserId({
      parentSelectedUserId: selectedUserId,
      pathName: ModalPath.GroupClassList,
    });
  }, [analyticsSource, selectedUserId, track]);

  const { externalId: appUserId, isSupport: isAppUserSupport } =
    useAppUserSelector();

  const {
    data,
    groupCardItems,
    isFetching: isGroupsFetching,
    isLoading: isGroupsLoading,
    isRecommended,
    onRefresh,
    registeredGroupCount,
    shouldRenderCarousel,
    shouldRenderEmptyComponent,
    shouldRenderSpinner,
    shouldShowMostPopularText,
    shouldShowViewAllCard,
  } = useGroupClassCarouselContent({
    selectedUserId,
    variant,
  });

  const shouldShowBlurb =
    isGroupClassRedesignEnabled &&
    variant === GroupClassCarouselVariantValues.promotedGroups &&
    registeredGroupCount <= 1;

  const carouselContent = useMemo(() => {
    if (shouldRenderSpinner) {
      return (
        <VStack
          height={PreviewCardSize.Height}
          justifyContent="center"
          testID={joinTestIDs(
            ExploreTestID.GroupClassSeriesCarousel,
            ExploreTestID.LoadingState,
          )}
        >
          <Spinner size="small" />
        </VStack>
      );
    }

    if (shouldRenderEmptyComponent) {
      return (
        <Box paddingHorizontal={24}>
          <YourGroupsEmptyListCard
            forWhom={forWhom}
            onButtonPress={handleSearchGroups}
            shouldShowSearchGroups={isSelectedUserInTreatment}
          />
        </Box>
      );
    }

    return (
      <GroupClassSeriesPreviewList
        analyticsSource={analyticsSource}
        forWhom={forWhom}
        groupCardItems={groupCardItems}
        groupClasses={data}
        isAppUserSupport={isAppUserSupport}
        isListFetching={isGroupsFetching}
        isListLoading={isGroupsLoading}
        isRecommended={isRecommended}
        onRefresh={onRefresh}
        patientId={selectedUserId}
        shouldShowMostPopularText={shouldShowMostPopularText}
        shouldShowViewAllCard={shouldShowViewAllCard}
        tabSource={tabSource}
        variant={variant}
      />
    );
  }, [
    analyticsSource,
    data,
    forWhom,
    handleSearchGroups,
    onRefresh,
    isAppUserSupport,
    isRecommended,
    isGroupsFetching,
    isGroupsLoading,
    isSelectedUserInTreatment,
    groupCardItems,
    selectedUserId,
    shouldRenderEmptyComponent,
    shouldRenderSpinner,
    shouldShowMostPopularText,
    shouldShowViewAllCard,
    tabSource,
    variant,
  ]);

  if (!shouldRenderCarousel) {
    return null;
  }

  return (
    <CarouselWrapper
      hasPaddingTop={hasPaddingTop}
      header={
        <SectionHeader
          appUserBadgeCount={0}
          appUserId={appUserId}
          isAppUserSupport={isAppUserSupport}
          isInlineHeader
          onUserChange={onUserChange}
          testID={buildCarouselTestID(variant)}
          title={title}
          userTabs={userTabs}
        />
      }
    >
      {shouldShowBlurb && (
        <Box padding={24} paddingTop={hasPaddingTop ? 16 : 0}>
          <ActionBar
            iconName={FAIconName.ExclamationTriangle}
            message={ActionBarMessages.RecommendedGroupsMessage}
            size="medium"
            testID={joinTestIDs(
              ExploreTestID.GroupClassSeriesCarousel,
              ExploreTestID.RecommendedGroupsBlurb,
            )}
            title={ActionBarMessages.RecommendedGroupTitle}
            variant="warning"
          />
        </Box>
      )}
      {carouselContent}
    </CarouselWrapper>
  );
};

export default GroupClassSeriesCarousel;
export type { GroupClassSeriesCarouselProps };
